import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import ReorderIcon from '@mui/icons-material/Reorder';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    DialogContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem as MuiMenuItem,
    Select,
    Skeleton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useDialog } from 'muibox';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BackNavigationButton from '../../components/common/BackNavigationButton';
import DialogAdaptive from '../../components/common/DialogAdaptive';
import LargeMaterialUISwitch from '../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../components/common/PageContainter';
import StyledCard from '../../components/common/StyledCard';
import StyledCardTitleBar from '../../components/common/StyledCardTitleBar';
import StyledDialogActions from '../../components/common/StyledDialogActions';
import useInventory from '../../hooks/useInventory';
import useMenu, { PpvMenuItem, UpsertPpvMenuItemPayload } from '../../hooks/useMenu';
import useSubscriber from '../../hooks/useSubscriber';
import useSubscriberMedia from '../../hooks/useSubscriberMedia';
import { OnlyFansVaultMedias } from '../../hooks/useSubscriberVaultAlbums';
import { SettingsContext } from '../../store/SettingsContext';
import { isSextforceActive } from '../../utils/common';
import MenuItemCard from './components/MenuItemCard';
import MenuItemEditorForm from './components/MenuItemEditorForm';
import MenuItemReorderModal from './components/MenuItemReorderModal';

const EMPTY_MENU_ITEMS: PpvMenuItem[] = [];

const StatsCard = (props: { title: string; value: number | string | React.ReactNode; icon: React.ReactNode; isLoading: boolean }) => {
    const { title, value, icon, isLoading } = props;

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: theme => theme.shape.borderRadius,
                borderColor: 'divider',
                height: '100%',
            }}
        >
            <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            bgcolor: 'action.hover',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            mt: 0.25,
                        }}
                    >
                        {icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ color: 'text.primary', lineHeight: 1.2 }}>
                            {title}
                        </Typography>
                        <Typography
                            key={value ? value.toString() : '0'}
                            variant="h5"
                            sx={{
                                mt: 0.5,
                                mb: 0.25,
                                fontWeight: 700,
                                color: 'text.primary',
                                lineHeight: 1.1,
                                animation: 'slideUpFade 0.4s ease-out',
                                '@keyframes slideUpFade': {
                                    '0%': { transform: 'translateY(12px)', opacity: 0 },
                                    '100%': { transform: 'translateY(0)', opacity: 1 },
                                },
                            }}
                        >
                            {isLoading ? <Skeleton width={80} height={40} /> : value?.toLocaleString() || 0}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const MenuAuthoring = () => {
    const settingsContext = useContext(SettingsContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dialog = useDialog();

    const { data: subscriber, isLoading: isSubscriberLoading, setServiceRunning, setServiceRunningLoading } = useSubscriber();

    const sextforceActive = isSextforceActive(subscriber);

    const [serviceActive, setServiceActive] = useState<boolean>(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [reorderOpen, setReorderOpen] = useState(false);
    const [howItWorksOpen, setHowItWorksOpen] = useState(false);
    const [editingMenuItem, setEditingMenuItem] = useState<PpvMenuItem | null>(null);
    const [isEditorSaving, setIsEditorSaving] = useState(false);
    const [activeLoadingId, setActiveLoadingId] = useState<string | null>(null);
    const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState<'all' | 'private' | 'public'>('all');
    const [inventoryMediaById, setInventoryMediaById] = useState<Record<number, OnlyFansVaultMedias.RestructuredResponseItem>>({});
    const { menuItemsQuery, menuSalesTotalQuery, createMenuItem, updateMenuItem, deleteMenuItem } = useMenu();
    const { data: contentInventory } = useInventory();
    const { getMediaCahcedReconstructedItem } = useSubscriberMedia();

    const menuItems = menuItemsQuery.data ?? EMPTY_MENU_ITEMS;
    const filteredMenuItems = menuItems.filter(item => {
        const query = searchQuery.trim().toLowerCase();
        const matchesSearch = !query || item.name.toLowerCase().includes(query) || item.message.toLowerCase().includes(query);
        const matchesType = typeFilter === 'all' ? true : typeFilter === 'public' ? item.public === true : item.public === false;
        return matchesSearch && matchesType;
    });

    // Sum all the sent counts from the menu items to get the total messages received in the last minute
    const totalSentCount = menuItems.reduce(
        (counter, item) => {
            counter.count += item.sentCount;
            return counter;
        },
        { count: 0 },
    );

    // Sum all the purchases counts from the menu items to get the total active chats being tracked (assuming each purchase corresponds to an active chat)
    const totalPurchaseCount = menuItems.reduce(
        (counter, item) => {
            counter.count += item.purchaseCount;
            return counter;
        },
        { count: 0 },
    );

    useEffect(() => {
        if (!subscriber || !sextforceActive) {
            setServiceActive(false);
            return;
        }

        if (subscriber?.sextforce?.ppvMenu?.active !== undefined) {
            setServiceActive(
                'active' in subscriber.sextforce.ppvMenu && typeof subscriber.sextforce.ppvMenu.active === 'boolean'
                    ? subscriber.sextforce.ppvMenu.active
                    : true,
            );
            return;
        }
        setServiceActive(false);
    }, [subscriber, sextforceActive]);

    const openCreateDialog = () => {
        setEditingMenuItem(null);
        setEditorOpen(true);
    };

    const openEditDialog = (item: PpvMenuItem) => {
        setEditingMenuItem(item);
        setEditorOpen(true);
    };

    const closeEditorDialog = () => {
        if (isEditorSaving) {
            return;
        }
        setEditorOpen(false);
        setEditingMenuItem(null);
    };

    const handleSave = async (menuItemId: string | null, payload: UpsertPpvMenuItemPayload) => {
        setIsEditorSaving(true);
        try {
            const response = menuItemId ? await updateMenuItem(menuItemId, payload) : await createMenuItem(payload);
            if (!response) {
                enqueueSnackbar('Unable to save PPV menu item', { variant: 'error' });
                return;
            }
            await menuItemsQuery.refetch();
            enqueueSnackbar('PPV menu item saved', { variant: 'success' });
            closeEditorDialog();
        } finally {
            setIsEditorSaving(false);
        }
    };

    const handleDelete = async (item: PpvMenuItem, source: 'list' | 'editor') => {
        await dialog
            .confirm({
                title: 'Delete Menu Item?',
                message: `This will deactivate #${item.command}.`,
                ok: { color: 'error', text: 'Delete', variant: 'contained' },
            })
            .then(async () => {
                if (source !== 'editor') {
                    setDeleteLoadingId(item.id);
                }

                const deleted = await deleteMenuItem(item.id);
                if (!deleted) {
                    enqueueSnackbar('Unable to delete menu item', { variant: 'error' });
                    return;
                }
                await menuItemsQuery.refetch();
                enqueueSnackbar('PPV menu item deleted', { variant: 'success' });
                if (source === 'editor') {
                    closeEditorDialog();
                }
            })
            .catch(() => null)
            .finally(() => {
                if (source !== 'editor') {
                    setDeleteLoadingId(null);
                }
            });
    };

    const toggleItemActive = async (item: PpvMenuItem) => {
        setActiveLoadingId(item.id);
        try {
            const response = await updateMenuItem(item.id, { active: !item.active });
            if (!response) {
                enqueueSnackbar('Unable to update active state', { variant: 'error' });
                return;
            }
            await menuItemsQuery.refetch();
            enqueueSnackbar(`Menu item #${item.command} ${response.active ? 'enabled' : 'disabled'}`, { variant: 'success' });
        } finally {
            setActiveLoadingId(null);
        }
    };

    useEffect(() => {
        const loadLinkedInventoryMedia = async () => {
            if (!subscriber || !contentInventory?.length || !menuItems.length) {
                setInventoryMediaById(prev => (Object.keys(prev).length ? {} : prev));
                return;
            }

            const linkedVersions = menuItems
                .map(menuItem => {
                    const inventory = contentInventory.find(entry => entry.inventoryId === menuItem.inventoryId);
                    return inventory?.versions.find(version => version.inventorySubId === menuItem.inventorySubId);
                })
                .filter(Boolean);

            const mediaIds = Array.from(
                new Set(
                    linkedVersions.flatMap(version =>
                        (version?.lockedMedias ?? [])
                            .map(media => media.mediaId)
                            .filter((mediaId): mediaId is number => typeof mediaId === 'number'),
                    ),
                ),
            );

            if (!mediaIds.length) {
                setInventoryMediaById({});
                return;
            }

            const resolved = await Promise.all(
                mediaIds.map(async mediaId => [mediaId, await getMediaCahcedReconstructedItem(subscriber, mediaId)] as const),
            );
            const next: Record<number, OnlyFansVaultMedias.RestructuredResponseItem> = {};
            resolved.forEach(([mediaId, media]) => {
                if (media) {
                    next[mediaId] = media;
                }
            });
            setInventoryMediaById(prev => {
                const prevKeys = Object.keys(prev);
                const nextKeys = Object.keys(next);
                if (
                    prevKeys.length === nextKeys.length &&
                    prevKeys.every(key => prev[key as unknown as number] === next[key as unknown as number])
                ) {
                    return prev;
                }
                return next;
            });
        };

        loadLinkedInventoryMedia();
    }, [contentInventory, menuItems, subscriber]);

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} spacing={2}>
                        <Typography variant="h5">
                            <BackNavigationButton url={`/subscribers/${subscriber?._id}/${settingsContext.services.inventory.homeUrl}`} />
                            PPV Menu for {subscriber.username}
                        </Typography>
                    </Stack>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="PPV Menu" />
                    </StyledCard>

                    <StyledCard>
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs={12}>
                                <Typography variant="body1" color="text.secondary">
                                    Create hashtag commands that fans can use to purchase content directly from the chat. Each menu item is
                                    linked to a specific template in your inventory, so make sure to create your{' '}
                                    <Link
                                        to={`/subscribers/${subscriber?._id}/${settingsContext.services.inventory.homeUrl}`}
                                        style={{
                                            textDecoration: 'none',
                                            fontWeight: 600,
                                        }}
                                    >
                                        templates
                                    </Link>{' '}
                                    first before creating menu items.
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="medium"
                                    fullWidth
                                    startIcon={<HelpOutlineIcon />}
                                    onClick={() => setHowItWorksOpen(true)}
                                >
                                    How it works
                                </Button>
                            </Grid>
                            <Grid item xs>
                                <span>PPV Menu is currently </span>
                                {serviceActive ? (
                                    <span style={{ color: theme.palette.success.dark }}>running</span>
                                ) : (
                                    <span style={{ color: theme.palette.error.main }}>not running</span>
                                )}
                                <span>.</span>
                            </Grid>
                            <Grid item xs="auto">
                                {isSubscriberLoading || setServiceRunningLoading ? (
                                    <Skeleton width={'115px'} height={'51px'} />
                                ) : (
                                    <LargeMaterialUISwitch
                                        checked={serviceActive}
                                        onChange={e => {
                                            setServiceRunning('sextforce.ppvMenu', e.target.checked);
                                        }}
                                        color="primary"
                                    />
                                )}
                            </Grid>
                            {subscriber && subscriber.ofSubscriptionPrice !== 0 && !sextforceActive && (
                                <Grid item xs={12}>
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        Sextforce service must be active to use this feature
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </StyledCard>

                    <StyledCard noCard>
                        <Stack direction="column" spacing={1}>
                            <Button
                                variant="contained"
                                color="secondary"
                                fullWidth
                                size="large"
                                startIcon={<AddIcon />}
                                onClick={openCreateDialog}
                            >
                                Add New PPV Menu
                            </Button>
                            <Button
                                variant="contained"
                                color="info"
                                fullWidth
                                size="large"
                                startIcon={<ReorderIcon />}
                                disabled={menuItems.length === 0}
                                onClick={() => setReorderOpen(true)}
                            >
                                Reorder menu
                            </Button>
                        </Stack>
                    </StyledCard>

                    {/* Stats */}
                    <StyledCard noCard>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <StatsCard
                                    title="Sent"
                                    value={totalSentCount.count}
                                    icon={<SendOutlinedIcon sx={{ fontSize: 26 }} />}
                                    isLoading={menuItemsQuery.isLoading}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <StatsCard
                                    title="Purchased"
                                    value={totalPurchaseCount.count}
                                    icon={<ShoppingBagOutlinedIcon sx={{ fontSize: 26 }} />}
                                    isLoading={menuItemsQuery.isLoading}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <StatsCard
                                    title="Revenue"
                                    value={`$${Number(menuSalesTotalQuery.data?.totalPurchaseAmount || 0).toFixed(2)}`}
                                    icon={<MonetizationOnOutlinedIcon sx={{ fontSize: 26 }} />}
                                    isLoading={menuSalesTotalQuery.isLoading}
                                />
                            </Grid>
                        </Grid>
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title={`PPV Menus (${(menuItems?.length || 0).toLocaleString()})`}
                            isLoading={menuItemsQuery.isLoading}
                        />
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <Card>
                            <CardContent>
                                <Stack spacing={2}>
                                    {/* Search */}
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                                        <TextField
                                            label="Search"
                                            size="small"
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            sx={{ bgcolor: 'background.paper', flex: 1, minWidth: 0 }}
                                        />
                                        <FormControl size="small" sx={{ width: 'fit-content', minWidth: 0, flexShrink: 0 }}>
                                            <InputLabel id="menu-type-filter-label">Type</InputLabel>
                                            <Select
                                                labelId="menu-type-filter-label"
                                                label="Type"
                                                value={typeFilter}
                                                onChange={e => setTypeFilter(e.target.value as 'all' | 'private' | 'public')}
                                                sx={{ minWidth: 0 }}
                                            >
                                                <MuiMenuItem value="all">All</MuiMenuItem>
                                                <MuiMenuItem value="private">Private</MuiMenuItem>
                                                <MuiMenuItem value="public">Public</MuiMenuItem>
                                            </Select>
                                        </FormControl>
                                    </Stack>

                                    {/* Menu items */}
                                    {filteredMenuItems.map(item => (
                                        <MenuItemCard
                                            key={item.id}
                                            item={item}
                                            contentInventory={contentInventory}
                                            inventoryMediaById={inventoryMediaById}
                                            activeLoading={activeLoadingId === item.id}
                                            deleteLoading={deleteLoadingId === item.id}
                                            onEdit={openEditDialog}
                                            onToggleActive={toggleItemActive}
                                            onDelete={menuItem => handleDelete(menuItem, 'list')}
                                        />
                                    ))}
                                    {menuItems.length === 0 && <Typography variant="body2">No menu items yet.</Typography>}
                                    {menuItems.length > 0 && filteredMenuItems.length === 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            No menu items match your search and type filter.
                                        </Typography>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </StyledCard>

                    <DialogAdaptive open={editorOpen} onClose={closeEditorDialog} fullScreen={isMobile}>
                        <StyledCardTitleBar title={editingMenuItem ? `Edit #${editingMenuItem.command}` : 'Create PPV Menu Item'} />
                        <MenuItemEditorForm
                            menuItem={editingMenuItem}
                            subscriber={subscriber}
                            onSave={handleSave}
                            onCancel={closeEditorDialog}
                            isSaving={isEditorSaving}
                        />
                    </DialogAdaptive>

                    <DialogAdaptive open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} fullScreen={isMobile}>
                        <StyledCardTitleBar title="How PPV Menu works" />
                        <DialogContent>
                            <Typography variant="body1" gutterBottom>
                                Create and manage your PPV menu items here. These are the commands that your fans can use to purchase
                                content directly from the chat.
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                Each menu item is linked to a specific{' '}
                                <Link to="/inventory" style={{ textDecoration: 'none', fontWeight: 500 }}>
                                    Template item
                                </Link>
                                , so make sure to create your Templates first before creating menu items. You can also set up aliases for
                                each command, so fans can use different variations of the command to purchase the same item. For example, if
                                you have a command called #tease, you can set up aliases like #teasing or #teaseme, and they will all work
                                to purchase the same item.
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                Fans can send the <span style={{ fontWeight: 600 }}>#menu</span> command in the chat to receive a message
                                with all the available menu items and their commands, making it easy for them to see what's available and
                                make purchases without you having to promote each item individually.
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                Make the menu public to list your menu items on{' '}
                                <Link
                                    to={`https://onlypromote.me/`}
                                    style={{
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                    }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    OnlyPromote.Me
                                </Link>
                                .
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                By making your menu public, your menu items will be listed on OnlyPromote.Me, which is a free directory of
                                OnlyStruggles OnlyFans creators and their offerings. It is public and indexed by search engines. This can
                                help you get more exposure and attract new fans who are browsing the site for content to purchase. If you
                                prefer to keep your menu private, only fans who are already subscribed to your OnlyFans will be able to see
                                the menu and make purchases from it.
                            </Typography>
                        </DialogContent>
                        <StyledDialogActions>
                            <Button variant="contained" color="primary" onClick={() => setHowItWorksOpen(false)}>
                                Got it
                            </Button>
                        </StyledDialogActions>
                    </DialogAdaptive>

                    <MenuItemReorderModal
                        open={reorderOpen}
                        onClose={() => setReorderOpen(false)}
                        items={menuItems}
                        updateMenuItem={updateMenuItem}
                        onSaved={async () => {
                            await menuItemsQuery.refetch();
                        }}
                    />
                </>
            )}
        </PageContainer>
    );
};

export default MenuAuthoring;
